import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LegalDocument } from "@/components/LegalDocument";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldCheck, Loader2, FileText } from "lucide-react";

export function AgreementGate({ onAccepted }) {
  const { logout } = useAuth();
  const [status, setStatus] = useState(null);
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState(null);

  useEffect(() => { api.get("/legal/acceptance-status").then((r) => setStatus(r.data)); }, []);

  const accept = async () => {
    setSaving(true);
    try {
      await api.post("/legal/accept");
      toast.success("Thank you. Your agreement has been recorded successfully.");
      onAccepted();
    } catch { toast.error("Could not record acceptance. Please try again."); }
    setSaving(false);
  };

  const titles = status?.titles || {};
  const order = ["agreement", "terms", "privacy", "aup"];
  const items = order.filter((t) => titles[t]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6 py-10">
      <div className="w-full max-w-lg">
        <Logo size="lg" className="justify-center mb-6" />
        <div className="rounded-2xl border border-border bg-card p-8 shadow-sm">
          <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4"><ShieldCheck className="h-6 w-6 text-primary" /></div>
          <h1 className="text-2xl font-bold tracking-tight">Review your agreements</h1>
          <p className="text-muted-foreground mt-2 text-sm">Before managing your store, please review and accept the following. You cannot access your dashboard until you accept.</p>

          <div className="mt-6 space-y-2">
            {items.map((t) => (
              <button key={t} onClick={() => setView(t)} className="w-full flex items-center justify-between rounded-lg border border-border px-4 py-3 hover:border-primary/50 transition-colors text-left" data-testid={`gate-doc-${t}`}>
                <span className="flex items-center gap-2.5 text-sm font-medium"><FileText className="h-4 w-4 text-muted-foreground" /> {titles[t]}</span>
                <span className="text-xs text-primary">Read →</span>
              </button>
            ))}
          </div>

          <label className="flex items-start gap-3 mt-6 cursor-pointer">
            <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} className="h-5 w-5 rounded mt-0.5 shrink-0" data-testid="gate-accept-checkbox" />
            <span className="text-sm text-foreground/80">I have read and agree to the Drelik Store Owner Agreement, Terms &amp; Conditions, Privacy Policy, and Acceptable Use Policy.</span>
          </label>

          <div className="mt-6 flex gap-3">
            <Button variant="outline" onClick={logout} className="rounded-lg">Sign out</Button>
            <Button disabled={!checked || saving} onClick={accept} className="rounded-lg flex-1" data-testid="gate-accept-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Accept & continue"}
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="sr-only">{titles[view]}</DialogTitle></DialogHeader>
          {view && <LegalDocument docType={view} embedded />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
